import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#setupSellingPlanSync(closestSection);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#teardownSellingPlanSync(closestSection);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Re-apply selling plan price after variant update replaces price container
    this.#activeSellingPlanId = null;
    this.#scheduleSellingPlanCheck();
  };

  /** @type {string|null} */
  #activeSellingPlanId = null;
  /** @type {number|undefined} */
  #sellingPlanPollInterval;
  /** @type {boolean} */
  #sellingPlanSyncActive = false;

  #setupSellingPlanSync(section) {
    if (!this.querySelector('script[data-selling-plan-data]')) return;
    this.#sellingPlanSyncActive = true;
    this.#sellingPlanPollInterval = setInterval(() => this.#syncSellingPlanPrice(), 300);
  }

  #teardownSellingPlanSync(_section) {
    this.#sellingPlanSyncActive = false;
    clearInterval(this.#sellingPlanPollInterval);
  }

  #scheduleSellingPlanCheck = () => {
    setTimeout(() => this.#syncSellingPlanPrice(), 100);
  };

  #syncSellingPlanPrice() {
    if (!this.#sellingPlanSyncActive) return;
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const input = /** @type {HTMLInputElement|null} */ (section.querySelector('input[name="selling_plan"]'));
    const planId = input?.value || null;

    if (planId === this.#activeSellingPlanId) return;
    this.#activeSellingPlanId = planId;

    const data = this.#getSellingPlanData();
    if (!data) return;

    if (planId) {
      this.#applySellingPlanPrice(data, planId);
    } else {
      this.#restoreRegularPrice(data);
    }
  }

  /**
   * @param {Object} data
   * @param {string} planId
   */
  #applySellingPlanPrice(data, planId) {
    const variantId = this.#getCurrentVariantId();
    const variant = data.allocations[variantId];
    if (!variant) return;

    const allocation = variant.sellingPlans[planId];
    if (!allocation) return;

    const format = data.useCurrency ? data.moneyWithCurrencyFormat : data.moneyFormat;
    const salePrice = formatMoney(allocation.price, format, data.currency);
    const comparePrice = formatMoney(allocation.compareAtPrice, format, data.currency);

    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const regular = container.querySelector('.price__regular');
    const sale = container.querySelector('.price__sale');

    if (regular) regular.classList.add('price__hidden');
    if (sale) {
      sale.classList.remove('price__hidden');
      const saleEl = sale.querySelector('.price-item--sale');
      const compareEl = sale.querySelector('.compare-at-price');
      if (saleEl) saleEl.textContent = salePrice;
      if (compareEl) compareEl.textContent = comparePrice;
    }
  }

  /** @param {Object} data */
  #restoreRegularPrice(data) {
    const variantId = this.#getCurrentVariantId();
    const variant = data.allocations[variantId];
    if (!variant) return;

    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const regular = container.querySelector('.price__regular');
    const sale = container.querySelector('.price__sale');
    const format = data.useCurrency ? data.moneyWithCurrencyFormat : data.moneyFormat;

    if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
      if (regular) regular.classList.add('price__hidden');
      if (sale) {
        sale.classList.remove('price__hidden');
        const saleEl = sale.querySelector('.price-item--sale');
        const compareEl = sale.querySelector('.compare-at-price');
        if (saleEl) saleEl.textContent = formatMoney(variant.price, format, data.currency);
        if (compareEl) compareEl.textContent = formatMoney(variant.compareAtPrice, format, data.currency);
      }
    } else {
      if (regular) {
        regular.classList.remove('price__hidden');
        const priceEl = regular.querySelector('.price');
        if (priceEl) priceEl.textContent = formatMoney(variant.price, format, data.currency);
      }
      if (sale) sale.classList.add('price__hidden');
    }
  }

  #getSellingPlanData() {
    const script = this.querySelector('script[data-selling-plan-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent ?? '');
    } catch {
      return null;
    }
  }

  #getCurrentVariantId() {
    const section = this.closest('.shopify-section, dialog');
    const input = /** @type {HTMLInputElement|null} */ (section?.querySelector('input[name="id"]'));
    return input?.value || '';
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
